'use client'

import * as amplitude from '@amplitude/analytics-browser'
import keyBy from 'lodash/keyBy'
import mapValues from 'lodash/mapValues'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useMemo } from 'react'

import type {
  AddPromoCodeProperties,
  IdentifyProperties,
  SelectCtaProperties,
  SelectNavCtaProperties,
  SelectPencilBannerCtaProperties,
  SelectRailCtaProperties,
  SelectStickyFooterCtaProperties,
  SetPasswordProperties,
  SubmitEmailAddressProperties,
  SubmitOrderProperties,
} from '@src/ampli-web'
import { ampli } from '@src/ampli-web'
import { useRailItemContext } from '@src/components/Rail/RailItemContext'
import { configPublic } from '@src/config.public'
import { useEffectOnce } from '@src/hooks/useEffectOnce'
import { useOnMount } from '@src/hooks/useOnMount'
import { useSession } from '@src/hooks/useSession'
import { useLocale } from '@src/i18n/LocaleProvider'
import {
  type CreateOrderRequest,
  type SetShippingAddressRequest,
} from '@src/services/order.service'
import useGeolocationStore from '@src/stores/geolocationStore'
import {
  convertLineItemForAmplitude,
  convertLineItemForGTM,
  convertOrderForAmplitude,
  getPageGlobalProps,
  getPageURLProperties,
} from '@src/utils/analytics-helpers.util'
import { QUERY_PARAMS } from '@src/utils/constants.util'
import { hash } from '@src/utils/hash.util'
import { log } from '@src/utils/log.util'

import { cookieName } from './amplitude-helpers'
import { getBuilderPreviewMode } from './builder.util'
import { convertCentsToFloat, getCartAmounts } from './cart.util'
import { getIsServer } from './common.util'
import { resetCookie } from './cookies.util'

export const useIdentify = (
  key: keyof IdentifyProperties,
  value?:
    | IdentifyProperties[keyof IdentifyProperties]
    | Promise<IdentifyProperties[keyof IdentifyProperties]>
) => {
  const { live } = getBuilderPreviewMode()

  useEffect(() => {
    if (!live || !ampli.isLoaded || !value) return

    // synchronous values are sent immediately, before page view events
    function identifySync() {
      const isEmptyObject = Object.values({ ...value }).every((val) => val === undefined)
      if (isEmptyObject) return
      ampli.identify(undefined, { [key]: value })
      ampli.flush()
    }

    // asynchronous values could get sent either before or after page view events
    async function identifyAsync() {
      const finalValue = await value
      const isEmptyObject = Object.values({ ...finalValue }).every((val) => val === undefined)
      if (isEmptyObject) return
      ampli.identify(undefined, { [key]: finalValue })
      ampli.flush()
    }

    if (value instanceof Promise) {
      identifyAsync()
    } else {
      identifySync()
    }
  }, [key, live, value])
}

/**
 * Set marketing attribution properties in Amplitude.
 * Needed for custom campaign tracking keys not supported by default.
 * @see https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#click-id-parameters
 * @param key The key of the property to set (same as the query parameter key).
 */
export function useMarketingAttribution(key: keyof IdentifyProperties) {
  const { live } = getBuilderPreviewMode()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (!live || !ampli.isLoaded) return
    const value = searchParams.get(key)

    // first-touch attribution - set once when user session starts
    const identifyEvent = new amplitude.Identify()
    identifyEvent.setOnce(`initial_${key}`, value || 'EMPTY') // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
    ampli.client.identify(identifyEvent)

    // multi-touch attribution - set/updated when the query parameter is present
    if (value) {
      const identifyEvent = new amplitude.Identify()
      identifyEvent.set(key, value)
      ampli.client.identify(identifyEvent)
    }
  }, [key, live, searchParams])
}

/**
 * Initialize Amplitude SDK in the browser.
 */
export function useAnalytics() {
  const { live } = getBuilderPreviewMode()
  const { locale } = useLocale()
  const { user } = useSession()
  const geoLocation = useGeolocationStore((state) => state.geoLocation)

  useOnMount(() => {
    const { apiKey, logLevel } = configPublic.amplitude
    const { cookieDomain } = configPublic.general
    if (!live) return

    if (!ampli.isLoaded) {
      // Reset AMP cookie to ensure the amplitude cookie is decoded
      const ampCookieName = cookieName(apiKey, true)
      resetCookie(ampCookieName, `domain=${cookieDomain}; path=/`)

      ampli.load({
        client: {
          configuration: {
            logLevel,
            sessionTimeout: 3600000, // 60 minutes in milliseconds
            defaultTracking: {
              attribution: true,
              pageViews: true,
              sessions: true,
              formInteractions: false,
              fileDownloads: false,
            },
          },
          apiKey,
        },
      })

      // Expose amplitude globally so Digioh can send events on our behalf
      window.amplitude = ampli.client
    }
  })

  // Send UserIdentity to Amplitude
  useOnMount(() => {
    if (!user) return

    ampli.identify(user?.guid, {
      'membership state': user?.userMembershipState,
      roles: user?.roles,
      entitlements: user?.entitlements,
    })
  })

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const uspapi = window?.__uspapi || undefined
    if (typeof uspapi !== 'function') return

    function listenForDoNotSell() {
      let hasOptedOut = window.Osano.cm.getConsent()?.OPT_OUT === 'DENY'
      ampli.identify(undefined, {
        'do not sell': hasOptedOut,
      })

      uspapi('getUSPData', 1, (uspData: any, success: boolean) => {
        if (success) {
          hasOptedOut = uspData?.uspString === '1-Y-' || uspData?.uspString === '1YY-'
          ampli.identify(undefined, {
            'do not sell': hasOptedOut,
          })
        } else {
          log.error('Osano IAB failure', uspData)
        }
      })
    }
    listenForDoNotSell()
  }, [])

  const firstVisit = useMemo(() => {
    if (typeof window === 'undefined') return undefined

    const TIME_KEY = 'firstVisitTime'
    const ID_KEY = 'firstVisitId'

    let timestamp = Number(localStorage?.getItem(TIME_KEY))
    let id = Number(localStorage?.getItem(ID_KEY))

    if (!timestamp) {
      timestamp = Date.now()
      localStorage?.setItem(TIME_KEY, String(timestamp))
    }
    if (!id) {
      // 10 digit random number
      id = Math.floor(Math.random() * 9000000000) + 1
      localStorage?.setItem(ID_KEY, String(id))
    }

    return { timestamp, id }
  }, [])

  const userAgent = useMemo(() => {
    if (typeof window === 'undefined') return undefined

    const agent = navigator.userAgent.toString()
    return agent
  }, [])

  const hashes = useMemo(async () => {
    return {
      city: geoLocation?.city && (await hash(geoLocation?.city.toLowerCase())),
      region: geoLocation?.countryRegion && (await hash(geoLocation?.countryRegion.toLowerCase())),
      country: geoLocation?.country && (await hash(geoLocation?.country.toLowerCase())),
    }
  }, [geoLocation?.city, geoLocation?.country, geoLocation?.countryRegion])

  // listen to user properties
  useIdentify('site country', locale.countryCodeLowercase)
  useIdentify('site language', locale.lang)
  useIdentify('hashes', hashes)
  useIdentify('user agent', userAgent)
  useIdentify('first visit time', firstVisit?.timestamp)
  useIdentify('first visit id', firstVisit?.id)

  useAnalyticsConsole()
}

/**
 * Register analytics actions to the global object
 * - used for 3rd-party integrations (e.g. Digioh boxes)
 **/
export function useAnalyticsConsole() {
  const trackAddToCart = useTrackAddToCart()

  useEffect(() => {
    globalThis.actionsAnalytics.trackAddToCart = trackAddToCart
  }, [trackAddToCart])

  return null
}

/**
 * Returns object of common properties used across all Amplitude page view events.
 */
function useScreenViewProperties() {
  // NOTE: location.href is not updated before effects run on client side route changes
  // so we need to construct the url here using hooks from next.js. Also, this code runs on the
  // server before the client, but ultimately analytics events only fire on the client. So we're
  // just using siteMapUrl here as a fallback to avoid errors on the server.
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const searchParamsString = searchParams ? searchParams?.toString() : undefined
  const origin = getIsServer() ? configPublic.general.siteMapUrl : window.location.origin
  const url = new URL(`${origin}${pathname}${searchParamsString && '?' + searchParamsString}`)

  return getPageURLProperties(url.href)
}

/**
 * Track a generic Builder page view.
 *
 * @param pageType The value of the page's pageType field in Builder.
 * @param properties The value of the page's analyticsData field in Builder.
 */
export function useViewBuilderPage(
  pageType?: string,
  properties?: Record<string, any>,
  cart?: Commercetools.Cart | null
) {
  const screenViewProperties = useScreenViewProperties()
  const { live } = getBuilderPreviewMode()

  // We want to ensure pageType defaults to 'other', even if it's an empty string
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  pageType = pageType?.toLowerCase() || 'other'

  const isCartPage = pageType === 'cart'

  useEffectOnce(
    function () {
      if (!live) return
      const lineItems = cart?.lineItems ?? []
      ampli.viewCartPage({
        ...screenViewProperties,
        'line items': lineItems.map(convertLineItemForAmplitude),
      })
    },
    [],
    Boolean(isCartPage && cart)
  )

  useEffectOnce(
    function () {
      if (!live) return
      ampli.track({
        event_type: `view ${pageType} page`,
        event_properties: {
          ...screenViewProperties,
          ...properties,
        },
      })
    },
    [],
    !isCartPage
  )
}

/**
 * Track the Product Detail page (PDP) view.
 *
 * @param product The commercetools product data object.
 */
export function useViewProductDetailPage(product?: Product, selectedVariant?: Variant) {
  const screenViewProperties = useScreenViewProperties()
  const { live } = getBuilderPreviewMode()

  useOnMount(function () {
    if (!live || !product || !selectedVariant) return

    // get the query params that are relevant to the product selection
    const facetKeys = Object.keys(selectedVariant.facets)
    const selectionKeys = [...facetKeys, 'sku', 'quantity']
    const queryParams = new URLSearchParams(location.search)
    queryParams.forEach((value, key) => {
      if (!selectionKeys.includes(key)) queryParams.delete(key)
    })

    const urlProductSelections = Object.fromEntries(queryParams)

    ampli.viewProductDetailPage({
      ...screenViewProperties,
      'product key': product.key,
      'product type': product.productType,
      'product subtype': product.productSubtype,
      'url product selections': [urlProductSelections],
    })
  })
}

/**
 * Track the checkout page view.
 *
 * @param cart The commercetools cart object.
 */
export function useViewCheckoutPage(cart: Commercetools.Cart | null) {
  const screenViewProperties = useScreenViewProperties()
  const { live } = getBuilderPreviewMode()

  const lineItems = cart?.lineItems ?? []

  const hashes = useMemo(async () => {
    return {
      email: cart?.customerEmail && (await hash(cart?.customerEmail?.toLowerCase())),
    }
  }, [cart?.customerEmail])

  useIdentify('hashes', hashes)

  useOnMount(function () {
    if (!live) return
    const currency = cart?.totalPrice?.currencyCode ?? 'unknown'
    ampli.viewCheckoutPage({
      ...screenViewProperties,
      'cart total': convertCentsToFloat(cart?.totalPrice?.centAmount ?? 0),
      currency,
      'line items': lineItems.map(convertLineItemForAmplitude),
    })
  })
}

/**
 * Track the order confirmation page view.
 *
 * @param order The commercetools order object.
 */
export function useViewOrderConfirmationPage(order: Commercetools.Order | null) {
  const screenViewProperties = useScreenViewProperties()
  const { live } = getBuilderPreviewMode()

  useOnMount(async function () {
    if (!live) return
    // Send event to Amplitude
    ampli.viewOrderConfirmationPage({
      ...screenViewProperties,
      ...convertOrderForAmplitude(order),
    })
  })
}

export function useViewAffiliateStorefrontPage() {
  const screenViewProperties = useScreenViewProperties()
  const { live } = getBuilderPreviewMode()
  const searchParams = useSearchParams()
  const ambassadorId = searchParams?.get(QUERY_PARAMS.lcAmbassadorId)
  const ambassadorIdDecoded = atob(ambassadorId ?? '')?.split(':')?.[1]

  useOnMount(function () {
    if (!live) return
    ampli.viewAffiliateStorefront({
      ...screenViewProperties,
      'ambassador id': ambassadorId ?? 'unknown',
      'loudcrowd customer id': ambassadorIdDecoded ?? 'unknown',
    })
  })
}

/**
 * Track order purchase when users successfully created an order.
 *
 * @param order The commercetools order object.
 */
export function useTrackPurchase() {
  const { live } = getBuilderPreviewMode()

  return async ({ order }: { order: Commercetools.Order | null }) => {
    if (!live) return
    const lineItems = order?.lineItems ?? []
    const amounts = getCartAmounts(order)
    const promoCodes = order?.discountCodes?.length
      ? order?.discountCodes.map((d) => d.discountCode.obj?.code ?? 'unknown')
      : []
    const emailHashed = order?.customerEmail && (await hash(order?.customerEmail))

    // Send event to Google Tag Manager
    window.dataLayer.push({
      event: 'purchase',
      currency: order?.totalPrice.currencyCode ?? 'unknown',
      value: parseFloat((amounts.total / 100).toFixed(2)),
      transaction_id: order?.orderNumber ?? 'unknown',
      coupon: promoCodes.join(','),
      country: order?.country?.toUpperCase(),
      items: lineItems.map(convertLineItemForGTM),
      alt_customer_id: emailHashed,
      customer_flow: order?.custom?.fields?.customerFlow ?? 'unknown',
    })
  }
}

/**
 * Track the submission of a user's email address.
 *
 * @param properties Relevant event properties.
 */
export function useTrackSubmitEmailAddress() {
  const { live } = getBuilderPreviewMode()

  return async function (
    properties: Pick<SubmitEmailAddressProperties, 'submit email result'> & { email: string }
  ) {
    if (!live) return
    const { email, ...rest } = properties
    // Send event to Amplitude
    ampli.submitEmailAddress({
      ...getPageGlobalProps(location.href),
      ...rest,
    })
    ampli.flush()

    // Send event to Google Tag Manager
    const emailHashed = email && (await hash(email))
    window.dataLayer.push({
      event: 'email_confirmed',
      alt_customer_id: emailHashed,
      email_result: properties['submit email result'],
    })
  }
}

/**
 * Track the submission of the create account form.
 */
export function useTrackCreateAccount() {
  const { live } = getBuilderPreviewMode()

  return function () {
    if (!live) return
    ampli.createAccount({
      ...getPageGlobalProps(location.href),
    })
  }
}

/**
 * Track the opening of the Cart Flyout.
 * @param properties Relevant event properties.
 * @param cart The commercetools cart object.
 *
 */

export function useTrackCartFlyoutOpen() {
  const { live } = getBuilderPreviewMode()
  return function (cart: Commercetools.Cart | null) {
    if (!live) return
    const lineItems = cart?.lineItems ?? []
    // Send event to Amplitude
    ampli.openCartFlyout({
      ...getPageGlobalProps(location.href),
      'line items': lineItems.map(convertLineItemForAmplitude),
    })
  }
}

/**
 * Track the setting of a password for new users who have completed a guest checkout.
 *
 * @param properties Relevant event properties.
 */
export function useTrackSetPassword() {
  const { live } = getBuilderPreviewMode()

  return function (properties: Pick<SetPasswordProperties, 'error code' | 'result'>) {
    if (!live) return
    ampli.setPassword({
      ...getPageGlobalProps(location.href),
      ...properties,
    })
    return ampli.flush()
  }
}

/**
 * Track the submission of an order.
 *
 * @param properties Relevant event properties.
 */
export function useTrackSubmitOrder() {
  const { live } = getBuilderPreviewMode()

  return function (
    properties: Pick<SubmitOrderProperties, 'error code' | 'result' | 'billing same as shipping'>
  ) {
    if (!live) return
    ampli.submitOrder({
      ...getPageGlobalProps(location.href),
      ...properties,
    })
    return ampli.flush()
  }
}

/**
 * Track when item(s) are added to the cart.
 *
 * @param cart The commercetools cart object.
 * @param addedItems Array of skus/quantities added to the cart.
 * @param redirectUrl The URL the user was directed to after the items were added to the cart.
 */
export function useTrackAddToCart() {
  const { live } = getBuilderPreviewMode()

  return async function (
    cart: Commercetools.Cart,
    addedItems: Array<{ sku: string; quantity: number }>,
    redirectUrl?: string
  ) {
    if (!live) return
    // Get a subset of cart line items based on skus/quantities that were added in this action
    const quantitiesBySku = mapValues(keyBy(addedItems, 'sku'), 'quantity')
    const lineItems = cart?.lineItems ?? []
    const addedLineItems = lineItems.reduce<typeof lineItems>((acc, item) => {
      const quantity = item.variant.sku && quantitiesBySku[item.variant.sku]
      quantity && acc.push({ ...item, quantity })
      return acc
    }, [])

    const emailHashed = cart?.customerEmail && (await hash(cart?.customerEmail))
    const valueOfAddedLineItems = addedLineItems.reduce((acc, item) => {
      const itemValue = item.price?.value?.centAmount ?? 0 * item.quantity
      return acc + itemValue
    }, 0)
    const currency = cart.totalPrice.currencyCode ?? 'unknown'

    // Send event to Amplitude
    ampli.addToCart({
      ...getPageGlobalProps(location.href),
      amount: convertCentsToFloat(valueOfAddedLineItems),
      currency,
      'line items': addedLineItems.map(convertLineItemForAmplitude),
      'cta url': redirectUrl ?? 'unknown',
    })

    // Send event to Google Tag Manager
    window.dataLayer.push({
      event: 'add_to_cart',
      items: addedLineItems.map(convertLineItemForGTM),
      country: cart?.country?.toUpperCase(),
      alt_customer_id: emailHashed,
      currency: cart?.totalPrice.currencyCode ?? 'unknown',
      value: convertCentsToFloat(valueOfAddedLineItems),
    })
  }
}

/**
 * Track when item(s) are removed from the cart.
 *
 * @param removedItems Array of LineItem removed from the cart.
 */
export function useTrackRemoveFromCart() {
  const { live } = getBuilderPreviewMode()

  return async function (removedItems: Commercetools.LineItem[]) {
    if (!live) return

    // Send event to Amplitude
    ampli.removeFromCart({
      ...getPageGlobalProps(location.href),
      'line items': removedItems.map(convertLineItemForAmplitude),
    })
  }
}

/**
 * Track when a generic CTA was clicked.
 *
 * @param properties Relevant event properties.
 */
export function useTrackSelectCta() {
  const { live } = getBuilderPreviewMode()

  return function (properties: Pick<SelectCtaProperties, 'cta url'>) {
    if (!live) return
    ampli.selectCta({
      ...getPageGlobalProps(location.href),
      ...properties,
    })
  }
}

/**
 * Track when a main navigation CTA was clicked.
 *
 * @param properties Relevant event properties.
 */
export function useTrackSelectNavCta() {
  const { live } = getBuilderPreviewMode()

  return function (properties: Pick<SelectNavCtaProperties, 'cta url'>) {
    if (!live) return
    ampli.selectNavCta({
      ...getPageGlobalProps(location.href),
      ...properties,
    })
    return ampli.flush()
  }
}

/**
 * Track when a user chooses to be assigned to a partner from the lead wheel.
 */
export function useTrackEnterLeadWheel() {
  const { live } = getBuilderPreviewMode()

  return function () {
    if (!live) return
    ampli.enterLeadWheel({
      ...getPageGlobalProps(location.href),
    })
  }
}

/**
 * Tracks when a user adds a promo code during checkout.
 */
export function useTrackAddPromoCode() {
  const { live } = getBuilderPreviewMode()

  return function (properties: Pick<AddPromoCodeProperties, 'promo code' | 'promo code result'>) {
    if (!live) return

    ampli.addPromoCode({
      ...getPageGlobalProps(location.href),
      ...properties,
    })
  }
}

/**
 * Track when a main navigation CTA was clicked.
 *
 * @param properties Relevant event properties.
 */
export function useTrackSelectStickyFooterCta() {
  const { live } = getBuilderPreviewMode()

  return function (properties: Pick<SelectStickyFooterCtaProperties, 'cta url'>) {
    if (!live) return
    ampli.selectStickyFooterCta({
      ...getPageGlobalProps(location.href),
      ...properties,
    })
    return ampli.flush()
  }
}

/**
 * Track when a main navigation CTA was clicked.
 *
 * @param properties Relevant event properties.
 */
export function useTrackSelectPencilBannerCta() {
  const { live } = getBuilderPreviewMode()

  return function (properties: Pick<SelectPencilBannerCtaProperties, 'cta url'>) {
    if (!live) return
    ampli.selectPencilBannerCta({
      ...getPageGlobalProps(location.href),
      ...properties,
    })
    return ampli.flush()
  }
}

/**
 * Track when a CTA is clicked in a rail component.
 *
 * @param properties Relevant event properties.
 */
export function useTrackSelectRailCta() {
  const { live } = getBuilderPreviewMode()
  const railItem = useRailItemContext()
  const slug = railItem?.slug
  const position = railItem?.position

  return function (properties: Pick<SelectRailCtaProperties, 'cta url'>) {
    if (!live || !slug) return

    ampli.selectRailCta({
      ...getPageGlobalProps(location.href),
      ...properties,
      'rail slug': slug,
      'cta position': position ?? 0,
    })
    return ampli.flush()
  }
}

export async function handleIdentifyOnOrderSubmit(args: {
  billingAddress: CreateOrderRequest['orderDraft']['billingAddress']
  shippingAddress: SetShippingAddressRequest['address']
}) {
  const { billingAddress, shippingAddress } = args

  const firstName = (shippingAddress.firstName || billingAddress.firstName).toLowerCase()
  const lastName = (shippingAddress.lastName || billingAddress.lastName).toLowerCase()
  const country = (shippingAddress.country || billingAddress.country).toLowerCase()
  const state = (shippingAddress.state || billingAddress.state).toLowerCase()
  const city = (shippingAddress.city || billingAddress.city).toLowerCase()
  const postalCode = (shippingAddress.postalCode || billingAddress.postalCode).toLowerCase()

  const [firstNameHash, lastNameHash, countryHash, stateHash, cityHash, postalHash] =
    await Promise.all([
      hash(firstName),
      hash(lastName),
      hash(country),
      hash(state),
      hash(city),
      hash(postalCode),
    ])

  ampli.identify(undefined, {
    hashes: {
      'first name': firstNameHash,
      'last name': lastNameHash,
      country: countryHash,
      region: stateHash,
      city: cityHash,
      'postal code': postalHash,
    },
  })
}
/**
 * Tracks when the quantity selector in the cart page is clicked.
 *
 */
export function trackUpdateQuantity(lineItem?: Commercetools.LineItem) {
  const { live } = getBuilderPreviewMode()

  if (!live || !lineItem) return
  ampli.updateQuantity({
    ...getPageGlobalProps(location.href),
    'line items': [convertLineItemForAmplitude(lineItem)],
  })
  return ampli.flush()
}

/**
 * Used to send legacy utag/tealium events from SSE to Amplitude.
 */
type UtagLinkArgs = { tealium_event?: string; link_tracking?: string } & Record<string, any>
export function trackUtagEvent(args?: UtagLinkArgs) {
  const { tealium_event, link_tracking, ...rest } = args ?? {}
  const eventName = link_tracking?.replace('_', ' ') ?? tealium_event?.toLowerCase() ?? 'unknown'
  ampli.track({
    event_type: `sse: ${eventName}`,
    event_properties: rest,
  })
}

/**
 * Tracks when a user opens the chat window.
 */
export function trackOpenChat(isNewConversation: boolean) {
  ampli.openChat({
    'is new conversation': isNewConversation,
    ...getPageGlobalProps(location.href),
  })
}

/**
 * Tracks when a user starts a chat conversation.
 */
export function trackStartConversation(conversationId: string) {
  ampli.startConversation({
    'conversation id': conversationId,
    ...getPageGlobalProps(location.href),
  })
}

/**
 * Track the community page.
 * @param properties Relevant event properties.
 */
export function useViewCommunityPage(slView: string | null) {
  const { live } = getBuilderPreviewMode()
  const screenViewProperties = useScreenViewProperties()

  useEffect(() => {
    if (!live) return

    ampli.viewCommunityPage({
      ...screenViewProperties,
      section: String(slView),
    })
  }, [live, screenViewProperties, slView])
}
